import { useEffect, useRef, useState } from "react";
import { MessageSquare, Flag, Send, ShieldCheck, Wifi, WifiOff, Pencil, Trash2, ThumbsDown, Check, X } from "lucide-react";
import { toast } from "sonner";
import { api, errMsg, wsUrl } from "../lib/api";
import { short, timeAgo } from "../lib/format";
import { useApp } from "../context/AppContext";
import { Panel, Loading, Empty } from "./State";
import { Link } from "react-router-dom";
import { Avatar } from "./Members";
import { useAccount } from "wagmi";
import { ConnectMenu } from "./WalletButton";

const DEFAULT_CHANNELS = ["hive", "general", "eth", "launchpad", "listings"];

function useChannel(channel) {
  const [msgs, setMsgs] = useState(null);
  const [live, setLive] = useState(false);
  useEffect(() => {
    let ws, stop = false, retry;
    setMsgs(null);
    api.get(`/chat/${channel}/messages`).then((r) => setMsgs(r.data)).catch(() => setMsgs([]));
    const connect = () => {
      ws = new WebSocket(wsUrl(`/ws/chat/${channel}`));
      ws.onopen = () => setLive(true);
      ws.onclose = () => { setLive(false); if (!stop) retry = setTimeout(connect, 3000); };
      ws.onmessage = (ev) => {
        const d = JSON.parse(ev.data);
        if (d.type === "message") setMsgs((m) => (m || []).some((x) => x.id === d.message.id) ? m : [...(m || []), d.message].slice(-150));
        if (d.type === "hidden") setMsgs((m) => (m || []).filter((x) => x.id !== d.id));
        if (d.type === "edited") setMsgs((m) => (m || []).map((x) => (x.id === d.message.id ? d.message : x)));
        if (d.type === "votes") setMsgs((m) => (m || []).map((x) => (x.id === d.id ? { ...x, downvoters: d.downvoters } : x)));
      };
    };
    connect();
    return () => { stop = true; clearTimeout(retry); ws && ws.close(); };
  }, [channel]);
  return { msgs, setMsgs, live };
}

const Message = ({ m, me, onReport, onEdit, onDelete, onVote }) => {
  const [editing, setEditing] = useState(false);
  const [draft, setDraft] = useState(m.text);
  const mine = me && me === m.address;
  const votes = m.downvoters || [];
  const voted = me && votes.includes(me);
  const save = async (e) => { e.preventDefault(); if (await onEdit(m, draft)) setEditing(false); };
  return (
  <li data-testid={`chat-message-${m.id}`} className="group flex gap-2.5 rounded-lg px-2 py-2 hover:bg-white/[0.03]">
    <Link to={`/u/${m.address}`} data-testid={`chat-avatar-${m.id}`}><Avatar url={m.avatar_url} address={m.address} size={30} /></Link>
    <div className="flex-1 min-w-0">
    <div className="flex items-center gap-2 text-[11px]">
      <Link to={`/u/${m.address}`} data-testid={`chat-author-${m.id}`} className={`font-mono font-semibold hover:underline ${m.is_admin ? "text-lime" : "text-slate-300"}`}>{m.alias || short(m.address)}</Link>
      {m.is_admin ? <span className="flex items-center gap-0.5 rounded bg-lime/15 px-1 text-[9px] font-bold text-lime"><ShieldCheck className="h-2.5 w-2.5" /> MOD</span>
        : <span className="rounded bg-white/5 px-1 text-[9px] text-slate-500" title="Identity proven by wallet signature (SIWE)">SIWE</span>}
      <span className="text-slate-600 ml-auto">{timeAgo(m.created_at)}{m.edited_at && <span data-testid={`chat-edited-${m.id}`}> · edited</span>}</span>
      {mine ? (<>
        <button data-testid={`chat-edit-${m.id}`} onClick={() => { setDraft(m.text); setEditing(true); }} title="Edit" className="sm:opacity-0 group-hover:opacity-100 text-slate-500 hover:text-lime"><Pencil className="h-3 w-3" /></button>
        <button data-testid={`chat-delete-${m.id}`} onClick={() => onDelete(m)} title="Delete" className="sm:opacity-0 group-hover:opacity-100 text-slate-500 hover:text-danger"><Trash2 className="h-3 w-3" /></button>
      </>) : (<>
        <button data-testid={`chat-downvote-${m.id}`} onClick={() => onVote(m)} title="Thumbs down (3 removes the post)" className={`flex items-center gap-0.5 ${voted ? "text-danger" : votes.length ? "text-slate-400" : "sm:opacity-0 group-hover:opacity-100 text-slate-500"} hover:text-danger`}><ThumbsDown className="h-3 w-3" />{votes.length > 0 && <span data-testid={`chat-downvote-count-${m.id}`} className="font-mono text-[10px]">{votes.length}</span>}</button>
        <button data-testid={`chat-report-${m.id}`} onClick={() => onReport(m)} title="Report" className="sm:opacity-0 group-hover:opacity-100 text-slate-600 hover:text-danger"><Flag className="h-3 w-3" /></button>
      </>)}
    </div>
    {editing ? (
      <form onSubmit={save} className="mt-1 flex gap-1">
        <input data-testid={`chat-edit-input-${m.id}`} autoFocus onKeyDown={(e) => e.key === "Escape" && setEditing(false)} value={draft} maxLength={500} onChange={(e) => setDraft(e.target.value)} className="field py-1 text-[13px]" />
        <button data-testid={`chat-edit-save-${m.id}`} className="rounded bg-lime px-2 text-black"><Check className="h-3.5 w-3.5" /></button>
        <button type="button" onClick={() => setEditing(false)} className="rounded bg-white/10 px-2"><X className="h-3.5 w-3.5" /></button>
      </form>
    ) : <p className="text-[13px] text-slate-200 mt-0.5 break-words leading-snug">{m.text}</p>}
    </div>
  </li>
  );
};

export const ChatWall = ({ channels = DEFAULT_CHANNELS, initial = "hive", tall = false, bgLogo }) => {
  const [channel, setChannel] = useState(initial);
  const { msgs, setMsgs, live } = useChannel(channel);
  const [text, setText] = useState("");
  const [sending, setSending] = useState(false);
  const { user, signIn, social, signing } = useApp();
  const { isConnected } = useAccount();
  const visible = (msgs || []).filter((m) => !social.blocked.has(m.address));
  const end = useRef(null);
  useEffect(() => { const ul = end.current?.parentElement; if (ul) ul.scrollTop = ul.scrollHeight; }, [msgs]);

  const send = async (e) => {
    e.preventDefault();
    if (!text.trim()) return;
    setSending(true);
    try { await api.post(`/chat/${channel}/messages`, { text }); setText(""); }
    catch (er) { if (er?.response?.status !== 401) toast.error(errMsg(er)); } finally { setSending(false); }
  };
  const me = user?.address?.toLowerCase();
  const editMsg = async (m, t) => {
    try { const r = await api.patch(`/chat/messages/${m.id}`, { text: t }); setMsgs((x) => x.map((y) => (y.id === m.id ? r.data : y))); return true; }
    catch (er) { toast.error(errMsg(er)); return false; }
  };
  const deleteMsg = async (m) => {
    if (!window.confirm("Delete this message?")) return;
    try { await api.delete(`/chat/messages/${m.id}`); setMsgs((x) => x.filter((y) => y.id !== m.id)); toast.success("Message deleted"); }
    catch (er) { toast.error(errMsg(er)); }
  };
  const vote = async (m) => {
    if (!user) return toast.error("Sign in with your wallet to vote");
    try {
      const r = await api.post(`/chat/messages/${m.id}/downvote`);
      setMsgs((x) => r.data.removed ? x.filter((y) => y.id !== m.id) : x.map((y) => (y.id === m.id ? { ...y, downvoters: r.data.downvoters } : y)));
      if (r.data.removed) toast.success("Post removed by community votes");
    } catch (er) { toast.error(errMsg(er)); }
  };
  const report = async (m) => {
    if (!user) return toast.error("Sign in with your wallet to report");
    try { await api.post(`/chat/messages/${m.id}/report`, { reason: "spam" }); toast.success("Reported to moderators"); }
    catch (er) { toast.error(errMsg(er)); }
  };

  return (
    <Panel testid="chat-wall" title="HIVEVORA chat" icon={MessageSquare} className="flex flex-col" right={
      <span data-testid="chat-live-indicator" className={`flex items-center gap-1 text-[10px] font-mono ${live ? "text-lime" : "text-slate-500"}`}>{live ? <Wifi className="h-3 w-3" /> : <WifiOff className="h-3 w-3" />}{live ? "LIVE" : "OFFLINE"}</span>}>
      <div className="flex gap-1 overflow-x-auto px-4 pt-3 pb-1">
        {channels.map((c) => (
          <button key={c} data-testid={`chat-channel-${c}`} onClick={() => setChannel(c)} className={`shrink-0 rounded-full px-2.5 py-1 text-[11px] font-mono ${channel === c ? "bg-lime text-black font-bold" : "bg-white/5 text-slate-400 hover:text-white"}`}>
            #{c.startsWith("token-") ? short(c.slice(6)) : c}
          </button>
        ))}
      </div>
      <div className="relative">
        {bgLogo && <img src={bgLogo} alt="" aria-hidden data-testid="chat-bg-logo" className="pointer-events-none absolute left-1/2 top-1/2 h-48 w-48 -translate-x-1/2 -translate-y-1/2 rounded-full object-cover opacity-[0.09] grayscale-[30%]" />}
        <ul className={`relative overflow-y-auto px-3 py-2 ${tall ? "h-[560px]" : "h-[440px] xl:h-[520px]"}`} data-testid="chat-message-list">
          {msgs === null ? <Loading /> : !visible.length ? <Empty title="No messages yet" hint="Be the first to say hi. Be kind — no seed-phrase talk, no shilling." /> :
            visible.map((m) => <Message key={m.id} m={m} me={me} onReport={report} onEdit={editMsg} onDelete={deleteMsg} onVote={vote} />)}
          <div ref={end} />
        </ul>
      </div>
      <div className="border-t border-white/5 p-3">
        {user && !user.alias && (
          <Link to={`/u/${user.address}`} data-testid="chat-create-profile-link" className="mb-2 block text-[11px] text-lime hover:underline">Create your profile (nickname, image, bio) →</Link>
        )}
        {user ? (
          <form onSubmit={send} className="flex gap-2">
            <input data-testid="chat-message-input" value={text} maxLength={500} onChange={(e) => setText(e.target.value)} placeholder={`Message #${channel.startsWith("token-") ? "token" : channel}`} className="field py-2 text-sm" />
            <button data-testid="chat-message-send-button" disabled={sending} className="btn-lime px-3"><Send className="h-4 w-4" /></button>
          </form>
        ) : !isConnected ? (
          <div className="flex items-center justify-between gap-2" data-testid="chat-connect-prompt"><span className="text-xs text-slate-400">Connect a wallet to chat</span><ConnectMenu testid="chat-connect-wallet-button" /></div>
        ) : (
          <button data-testid="chat-sign-in-button" onClick={signIn} disabled={signing} className="w-full rounded-lg border border-lime/30 py-2 text-xs text-lime hover:bg-lime/10">{signing ? "Confirm the signature in your wallet…" : "Sign in to chat (one free signature)"}</button>
        )}
        <p className="mt-2 text-[10px] text-slate-600">Links limited to explorer/docs · 1 msg / 3s · 3 thumbs-down removes a post · reports reviewed by moderators</p>
      </div>
    </Panel>
  );
};
